import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import {
  IconButton,
  Dialog,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Post } from 'lib/types/posts';
import { useApi } from 'lib/contexts/ApplicationState';
import { POST_TYPES, POST_TYPE_OBJECTS } from 'lib/constants';
import { SensitiveContentTypes } from 'lib/types';

interface Props {
  onClose: () => void;
  post: Post;
}

interface UpdatePostData {
  account: string;
  statsPaused: boolean;
  sensitiveContent: SensitiveContentTypes;
  isLive: boolean;
  type: number;
  caption: string;
}

export const UpdatePostInfoModal = ({ onClose, post }: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<UpdatePostData>({
    account: post.account || '',
    statsPaused: post.statsPaused || false,
    sensitiveContent: post.sensitiveContent || null,
    isLive: post.isLive || true,
    type: post.type || POST_TYPES.IMAGE,
    caption: post.caption || '',
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field: keyof UpdatePostData) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>,
  ) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSwitchChange = (field: keyof UpdatePostData) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSensitiveContentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      sensitiveContent: isChecked ? 'admin' : null,
    }));
  };

  const handleSelectChange = (field: keyof UpdatePostData) => (
    event: { target: { value: unknown } },
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const onSave = async () => {
    if (!formData.account.trim()) {
      enqueueSnackbar('Account field is required', { variant: 'error' });
      return;
    }

    try {
      setIsSaving(true);
      await API.updatePost(post.id, formData);
      enqueueSnackbar('Post information updated successfully', { variant: 'success' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(
        err?.message || 'Something went wrong updating post information',
        { variant: 'error' },
      );
    } finally {
      setIsSaving(false);
    }
  };

  const postTypeOptions = Object.entries(POST_TYPE_OBJECTS).map(([, value]) => ({
    value: value.id,
    label: value.title,
  }));

  return (
    <Dialog fullWidth maxWidth="sm" open aria-labelledby="update-post-info-dialog-title">
      <div className="p-6">
        <div className="absolute right-3 top-3">
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </div>

        <h2 id="update-post-info-dialog-title" className="text-xl font-semibold mb-6">
          Update Post Information
        </h2>

        <Box className="space-y-4">
          <TextField
            fullWidth
            label="Account"
            value={formData.account}
            onChange={handleInputChange('account')}
            required
            variant="outlined"
          />

          <TextField
            fullWidth
            label="Caption"
            value={formData.caption}
            onChange={handleInputChange('caption')}
            multiline
            rows={3}
            variant="outlined"
          />

          <FormControl fullWidth variant="outlined">
            <InputLabel>Post Type</InputLabel>
            <Select
              value={formData.type}
              onChange={handleSelectChange('type')}
              label="Post Type"
            >
              {postTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={(
              <Switch
                checked={!!formData.sensitiveContent}
                onChange={handleSensitiveContentChange}
                color="primary"
              />
            )}
            label="Sensitive Content"
          />

          <FormControlLabel
            control={(
              <Switch
                checked={formData.isLive}
                onChange={handleSwitchChange('isLive')}
                color="primary"
              />
            )}
            label="Post is Live"
          />

          <FormControlLabel
            control={(
              <Switch
                checked={formData.statsPaused}
                onChange={handleSwitchChange('statsPaused')}
                color="primary"
              />
            )}
            label="Stats Paused"
          />

          <Box className="flex justify-end space-x-2 mt-6">
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={onSave}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </div>
    </Dialog>
  );
};
